import type { JSX } from 'react';
import { useMemo, useState, useEffect } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { Navigate, useParams } from 'react-router-dom';
import { useMq } from '@wistia/vhs';
// eslint-disable-next-line no-restricted-imports
import { UIProvider } from '@wistia/ui';
import { darken } from 'polished';
import type { WistiaPlayer } from '@wistia/wistia-player';
import { isNotNil } from '~/utilities/type-guards';
import { useInjectFonts } from '~/hooks/useInjectFonts';
import type { Font } from '~/utilities/fonts';
import type { ApolloQueryResult } from '@apollo/client';
import {
  AnonymousHeader,
  BrandingCell,
  ActionsCell,
} from '../../../components/Header/AnonymousHeader';
import { LoadingState } from './LoadingState';
import { AudioLayout } from './layouts/AudioLayout';
import { VideoLayout } from './layouts/VideoLayout';
import { ImageLayout } from './layouts/ImageLayout';
import { UnsupportedMediaTypeLayout } from './layouts/UnsupportedMediaTypeLayout';
import type { MediaPageCustomizations } from '../MediaPreview/customize/MediaPageCustomizationsContext';
import {
  useMediaPageCustomizationDispatch,
  useNullableMediaPageCustomizations,
} from '../MediaPreview/customize/MediaPageCustomizationsContext';
import { DownloadMediaButton } from './DownloadMediaButton';
import type { AnonymousMediaQuery } from './__gql/AnonymousMedia.gql.gen';
import { useAnonymousMediaQuery } from './__gql/AnonymousMedia.gql.gen';
import { TeamCommentsSidePanel } from './TeamCommentsSidePanel';
import type { MediaPreviewQuery } from '../MediaPreview/__gql/MediaPreview.gql.gen';
import { FreeBrandingBanner } from './FreeBrandingBanner';

const DARKEN_LINKS_AMOUNT = 0.08;

const GlobalStyle = createGlobalStyle`
  a {
    color: ${({ theme }) => theme.color.brandBlue500};

    &:hover,
    &:focus {
      color: ${({ theme }) => darken(DARKEN_LINKS_AMOUNT, theme.color.brandBlue500)};
    }
  }
`;

const MediaAndStuffBelowItContainer = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  padding-bottom: ${({ theme }) => theme.spacing.space08};
  row-gap: ${({ theme }) => theme.spacing.space06};
`;

const ScreenLayout = styled.div`
  display: flex;
`;

export type MediaPageData = {
  hasCustomizations: boolean;
  customizations?: MediaPageCustomizations;
};

export type AnonymousMediaScreenProps = {
  commentingDisabled?: boolean;
  doNotTrack?: boolean;
  fakeViewportMarginRightStyle?: string;
  fakeViewportMarginLeftStyle?: string;
  mediaPreviewData?: MediaPreviewQuery['media'];
};

type AnonymousMediaContentProps = AnonymousMediaScreenProps & {
  media: NonNullable<AnonymousMediaQuery['anonymousMedia']>;
  currentUser?: AnonymousMediaQuery['currentUser'];
  fetchMore: (arg: GenericRecord) => Promise<ApolloQueryResult<AnonymousMediaQuery>>;
};

export const AnonymousMediaContent = ({
  media,
  currentUser,
  fetchMore,
  commentingDisabled = false,
  doNotTrack = false,
  fakeViewportMarginRightStyle,
  fakeViewportMarginLeftStyle,
  mediaPreviewData,
}: AnonymousMediaContentProps): JSX.Element => {
  const { isXlAndUp } = useMq();

  const [videoEmbed, setVideoEmbed] = useState<WistiaPlayer | null>(null);

  const dispatch = useMediaPageCustomizationDispatch();
  const mediaPageCustomizations = useNullableMediaPageCustomizations();

  useEffect(() => {
    dispatch({
      type: 'SET_MEDIA_TYPE',
      payload: media.mediaType,
    });

    dispatch({
      type: 'SET_PLAYER_COLOR',
      payload: media.playerColor,
    });

    dispatch({
      type: 'SET_DOES_MEDIA_HAVE_TRANSCRIPT',
      payload: media.hasReadyTimeCodedTranscript,
    });

    let customizations = {};
    if (media.mediaPage?.customizations) {
      customizations = {
        ...media.mediaPage.customizations,
        publicCommentsSelection: media.publicCommentsSelection,
      };
    } else {
      customizations = {
        publicCommentsSelection: media.publicCommentsSelection,
      };
    }

    // Initially populate the local store with the media page customizations
    // dispatch will be a no-op when called without the MediaPageCustomizationContext on the logged-out view
    dispatch({
      type: 'INIT_CUSTOMIZATIONS',
      payload: customizations as MediaPageCustomizations,
    });
  }, [media, dispatch]);

  const fonts = useMemo(() => {
    const customizations = (media.mediaPage?.customizations ?? {}) as MediaPageCustomizations;
    const { fontFamily, fontUrl } = customizations;
    const result: Font[] = [];
    if (fontFamily) {
      result.push({ family: fontFamily, url: fontUrl ?? undefined });
    }
    return result;
  }, [media]);
  const { isLoadingCustomFonts } = useInjectFonts({
    fonts,
    isWaitingForCustomizationsToLoad: false,
  });

  if (isLoadingCustomFonts) {
    return <LoadingState />;
  }

  const {
    id,
    hashedId: mediaHashedId,
    mediaType,
    permissionsForCurrentContact: { canDownload },
    hasReadyTimeCodedTranscript,
    topLevelTeamComments,
    account,
  } = media;

  const { topLevelTeamComments: previewTopLevelTeamComments } = mediaPreviewData ?? {};

  // mediaPageCustomizations will be an empty object when called without the MediaPageCustomizationContext on the logged-out view
  const customizations =
    mediaPageCustomizations?.currentCustomizations ??
    (media.mediaPage?.customizations as MediaPageCustomizations | undefined);

  const showTeamCommentsSidePanel =
    customizations?.publicCommentsSelection === 'team' &&
    (isNotNil(topLevelTeamComments) || isNotNil(previewTopLevelTeamComments)) &&
    isXlAndUp;

  const shouldShowTranscriptTab = Boolean(
    hasReadyTimeCodedTranscript && customizations?.shouldShowTranscript,
  );

  const mediaForDisplay = mediaPreviewData ?? media;

  return (
    <ScreenLayout>
      <GlobalStyle />

      <div style={{ flexGrow: '1' }}>
        <AnonymousHeader>
          {!account.wistiaBrandingOptional && (
            <BrandingCell>
              <FreeBrandingBanner
                account={account}
                media={media}
                isSignedIn={isNotNil(currentUser)}
              />
            </BrandingCell>
          )}

          {!!canDownload && (
            <ActionsCell>
              <DownloadMediaButton mediaHashedId={mediaHashedId} />{' '}
            </ActionsCell>
          )}
        </AnonymousHeader>

        <MediaAndStuffBelowItContainer>
          {mediaType === 'Audio' && (
            <AudioLayout
              customizations={customizations}
              commentingDisabled={commentingDisabled}
              doNotTrack={doNotTrack}
              hashedId={mediaHashedId}
              media={mediaForDisplay}
              testId="anonymous-SPA-media-page-player"
              shouldShowTranscriptTab={shouldShowTranscriptTab}
              videoEmbed={videoEmbed}
              setVideoEmbed={setVideoEmbed}
              fetchMore={fetchMore}
            />
          )}
          {mediaType === 'Video' && (
            <VideoLayout
              customizations={customizations}
              commentingDisabled={commentingDisabled}
              doNotTrack={doNotTrack}
              fakeViewportMarginLeftStyle={fakeViewportMarginLeftStyle}
              fakeViewportMarginRightStyle={fakeViewportMarginRightStyle}
              hashedId={mediaHashedId}
              media={mediaForDisplay}
              testId="anonymous-SPA-media-page-player"
              shouldShowTranscriptTab={shouldShowTranscriptTab}
              videoEmbed={videoEmbed}
              setVideoEmbed={setVideoEmbed}
              fetchMore={fetchMore}
            />
          )}
          {mediaType === 'Image' && (
            <ImageLayout
              commentingDisabled={commentingDisabled}
              media={media}
            />
          )}
          {!['Image', 'Video', 'Audio'].includes(mediaType) && (
            <UnsupportedMediaTypeLayout media={media} />
          )}
        </MediaAndStuffBelowItContainer>
      </div>
      {showTeamCommentsSidePanel ? (
        <TeamCommentsSidePanel
          commentingDisabled={commentingDisabled}
          mediaId={id}
          mediaHashedId={mediaHashedId}
          videoEmbed={videoEmbed}
          teamComments={previewTopLevelTeamComments ?? topLevelTeamComments}
          fetchMore={fetchMore}
        />
      ) : null}
    </ScreenLayout>
  );
};

const AnonymousMediaScreen = (props: AnonymousMediaScreenProps): JSX.Element => {
  const { mediaHashedId } = useParams();

  if (mediaHashedId == null) {
    throw new Error('No mediaHashedId');
  }

  const redirect = (route: '/404' | '/500') => {
    return (
      <Navigate
        replace={false}
        to={route}
      />
    );
  };

  const { error, data, loading, fetchMore } = useAnonymousMediaQuery({
    variables: { hashedId: mediaHashedId },
    // now that the media page is in the SPA, we can make modifications on that page that will make our cache invalid,
    // this policy will ensure that we always update our cache if things have changed from the network
    // read more here: https://www.apollographql.com/docs/react/data/queries/#cache-and-network
    fetchPolicy: 'cache-and-network',
  });

  if ((!error && !data) || loading) {
    return <LoadingState />;
  }

  if (error) {
    return redirect('/500');
  }

  if (!data?.anonymousMedia) {
    return redirect('/404');
  }

  const { anonymousMedia: media, currentUser } = data;

  return (
    <UIProvider>
      <AnonymousMediaContent
        media={media}
        currentUser={currentUser}
        fetchMore={fetchMore}
        {...props}
      />
    </UIProvider>
  );
};

// eslint-disable-next-line import/no-default-export
export default AnonymousMediaScreen;
